import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { api } from '@/api/client';
import type { PagedResult } from '@/api/types';

/**
 * Binds a <DataGrid> to a paged endpoint.
 *
 * The wire format is the clean one — `{ page, pageSize, sortBy, sortDir, search,
 * filters }` in, `{ rows, total }` out — rather than DataTables'
 * `draw`/`start`/`length`/`order[0][column]`. Controllers translate to the
 * existing `DashboardDataService` signatures at the boundary, so no query or
 * row-scoping logic moves.
 *
 * Every Phase 3 and Phase 4 grid goes through here, which is why paging state,
 * the query key and the debounce live in one place instead of in each page.
 */

export interface ServerDataGridOptions {
  /** Endpoint returning PagedResult, e.g. '/api/takata-calls'. */
  endpoint: string;
  /** Distinguishes this grid's cache entries. */
  queryKey: readonly unknown[];
  initialPageSize?: number;
  initialSorting?: SortingState;
  /** Free-text search, already debounced by the caller if it comes from typing. */
  search?: string;
  /** Per-column filters; empty and undefined values are dropped. */
  filters?: Record<string, string | undefined>;
  enabled?: boolean;
}

export function useServerDataGrid<TRow>({
  endpoint,
  queryKey,
  initialPageSize = 25,
  initialSorting = [],
  search,
  filters,
  enabled = true,
}: ServerDataGridOptions) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  // Drop empty filter values so they do not become `?country=` on the wire,
  // which the server would have to distinguish from "not filtered".
  const activeFilters = useMemo(() => {
    if (!filters) return undefined;
    const entries = Object.entries(filters).filter(([, value]) => value != null && value !== '');
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  }, [filters]);

  const requestBody = useMemo(
    () => ({
      // The API is 1-based; the table is 0-based.
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sorting[0]?.id,
      sortDir: sorting[0] ? ((sorting[0].desc ? 'desc' : 'asc') as 'asc' | 'desc') : undefined,
      search: search || undefined,
      filters: activeFilters,
    }),
    [pagination, sorting, search, activeFilters],
  );

  const query = useQuery<PagedResult<TRow>>({
    queryKey: [...queryKey, requestBody],
    queryFn: () => api.post<PagedResult<TRow>>(endpoint, requestBody),
    enabled,
    // Holding the previous page while the next one loads keeps the grid from
    // collapsing to an empty table on every paging click.
    placeholderData: keepPreviousData,
  });

  // Any change to what is being filtered or searched invalidates the current
  // page number: page 5 of the old result set is meaningless in the new one.
  const resetToFirstPage = useCallback(() => {
    setPagination((current) => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
  }, []);

  return {
    /** Spread straight onto <DataGrid>. */
    gridProps: {
      rows: query.data?.rows ?? [],
      total: query.data?.total ?? 0,
      pagination,
      onPaginationChange: setPagination,
      sorting,
      onSortingChange: setSorting,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
    },
    query,
    requestBody,
    resetToFirstPage,
  };
}
